const LAST_WORKSPACE_KEY = "aera_last_workspace";
const WORKSPACE_SELECTED_EVENT = "aera:workspace-selected";
const SESSION_PICK_KEY = "aera_workspace_pick_session";

/** @deprecated Per-service keys from an earlier build; read once when migrating. */
const LEGACY_KEY_PREFIX = "aera_last_workspace_";

/** Persist the workspace the user last selected (LIFF /go resolver, any navigation). */
export function persistLastWorkspace(workspaceId: string): void {
  if (typeof window === "undefined") return;
  const wid = workspaceId?.trim();
  if (!wid) return;
  try {
    localStorage.setItem(LAST_WORKSPACE_KEY, wid);
  } catch {
    // ignore quota / private mode
  }
}

/** Live `workspaceId` from the address bar — Next `useSearchParams` can lag. */
export function readLiveWorkspaceId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return new URLSearchParams(window.location.search).get("workspaceId")?.trim() || null;
  } catch {
    return null;
  }
}

export function readLiveSearch(): string {
  if (typeof window === "undefined") return "";
  return window.location.search.replace(/^\?/, "");
}

/** Shop the user picked this tab — landing must not jump to another hired shop. */
export function readSessionWorkspacePick(): string | null {
  if (typeof window === "undefined") return null;
  try {
    return sessionStorage.getItem(SESSION_PICK_KEY)?.trim() || null;
  } catch {
    return null;
  }
}

/** User picked a shop — persist and notify the Bill-Lin shell in the same tick. */
export function emitWorkspaceSelected(workspaceId: string): void {
  const wid = workspaceId?.trim();
  if (!wid) return;
  persistLastWorkspace(wid);
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SESSION_PICK_KEY, wid);
  } catch {
    // ignore quota / private mode
  }
  window.dispatchEvent(new CustomEvent(WORKSPACE_SELECTED_EVENT, { detail: wid }));
}

export function subscribeWorkspaceSelected(
  handler: (workspaceId: string) => void,
): () => void {
  if (typeof window === "undefined") return () => {};
  const onPick = (event: Event) => {
    const id = (event as CustomEvent<string>).detail?.trim();
    if (id) handler(id);
  };
  window.addEventListener(WORKSPACE_SELECTED_EVENT, onPick);
  return () => window.removeEventListener(WORKSPACE_SELECTED_EVENT, onPick);
}

export function readLastWorkspace(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const current = localStorage.getItem(LAST_WORKSPACE_KEY)?.trim();
    if (current) return current;

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(LEGACY_KEY_PREFIX)) continue;
      const legacy = localStorage.getItem(key)?.trim();
      if (legacy) {
        localStorage.setItem(LAST_WORKSPACE_KEY, legacy);
        return legacy;
      }
    }
    return null;
  } catch {
    return null;
  }
}
